import { Icon24GiftOutline } from '@vkontakte/icons';
import { Spacing, Text, Title } from '@vkontakte/vkui';
import coverImage from 'img/discount-in-outflow.png';
import coverImageMobile from 'img/discount-in-outflow-mobile.png';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { emitAnalyticEvent } from 'reactApp/appHelpers/experimentAnalytic';
import { AnalyticEventNames } from 'reactApp/appHelpers/experimentAnalytic/eventNames';
import { discountInOutflowValue } from 'reactApp/appHelpers/featuresHelpers/features/discountInOutflow';
import { WhatsNewDialog } from 'reactApp/components/WhatsNewDialog/WhatsNewDialog';
import type { BillingDataSubscriptionInfo } from 'reactApp/modules/billingData/billingData.types';
import { EnvironmentSelectors } from 'reactApp/modules/environment/environment';
import {
    ESubscriptionModalAction,
    useSendSubscriptionModalAnalytics,
} from 'reactApp/sections/SubscriptionsPage/ui/SubscriptionModal/SubscriptionModal.analytics';
import type { Product } from 'reactApp/types/Billing';
import { Button } from 'reactApp/ui/Button/Button';
import { DISCOUNT_IN_OUTFLOW_PERIODS_TABLE, discountInOutflowTextFormatter } from 'reactApp/ui/DiscountInOutflow/DiscountInOutflow.const';
import { MobileDialog } from 'reactApp/ui/Mobile/MobileDialog/MobileDialog';

import styles from './DiscountInOutflowModal.css';

export interface DiscountInOutflowModalProps {
    discountPrice?: number;
    product: Product;
    subscription: BillingDataSubscriptionInfo;
    onShow?: () => void;
    onClose?: () => void;
    onCancel: () => void;
    onSuccess: () => void;
}

const decoration = (
    <svg fill="none" height="35px" width="37px" viewBox="0 0 37 35" xmlns="http://www.w3.org/2000/svg">
        <path d="m18.835 18.152c-3.0127-2.4225-6.122-4.7936-10.06-7.9581" stroke="#B8FC75" strokeLinecap="square" strokeWidth="5" />
        <path d="m13.838 28.974-8.9275 0.3594" stroke="#B8FC75" strokeLinecap="square" strokeWidth="5" />
        <path d="m28.384 10.533-0.2934-7.2975" stroke="#B8FC75" strokeLinecap="square" strokeWidth="5" />
    </svg>
);

export function DiscountInOutflowModal({
    onClose,
    onCancel,
    onSuccess,
    onShow,
    product,
    subscription,
    discountPrice,
}: DiscountInOutflowModalProps) {
    const isMobile = EnvironmentSelectors.isPhone();
    const scrollRef = useRef<HTMLDivElement>(null);

    const sendAnalytic = useSendSubscriptionModalAnalytics(subscription);

    useEffect(() => {
        emitAnalyticEvent(AnalyticEventNames.MY_TARIFF_CANCEL_SALE_SHOW);
        sendAnalytic(ESubscriptionModalAction.cancelSaleShow);
        onShow?.();
    }, []);

    const onCloseAction = useCallback(() => {
        emitAnalyticEvent(AnalyticEventNames.MY_TARIFF_CANCEL_SALE_CLOSE);
        sendAnalytic(ESubscriptionModalAction.cancelSaleClose);
        onClose?.();
    }, [onClose]);

    const onCancelAction = useCallback(() => {
        emitAnalyticEvent(AnalyticEventNames.MY_TARIFF_CANCEL_SALE_REJECT);
        sendAnalytic(ESubscriptionModalAction.cancelSaleReject);
        onCancel?.();
    }, [onCancel]);

    const onSuccessAction = useCallback(() => {
        emitAnalyticEvent(AnalyticEventNames.MY_TARIFF_CANCEL_SALE_GET);
        sendAnalytic(ESubscriptionModalAction.cancelSaleGet);
        onSuccess?.();
    }, [onSuccess]);

    const title = `Закрепим за вами скидку ${discountInOutflowValue}%`;
    const text = <>Она будет действовать для тарифа, который подключен сейчас, и&nbsp;отключится только вместе с&nbsp;ним</>;

    const content = useMemo(() => {
        return (
            <div className={styles.content}>
                <Spacing size={12} />
                <Text className={styles.text}>{text}</Text>
                <Spacing size={24} />
            </div>
        );
    }, [text]);
    const imgBanner = useMemo(() => {
        return (
            <div className={styles.imageBanner}>
                <div className={styles.decoration}>{decoration}</div>
                <div className={styles.discountInfo}>
                    <Icon24GiftOutline />
                    <Text className={styles.discountInfoText}>-{discountInOutflowValue}%</Text>
                </div>
                <Spacing size={24} />
                <div className={styles.priceContainer}>
                    <Text className={styles.priceText}>
                        <Text className={styles.price}>{discountInOutflowTextFormatter.format(discountPrice || 0)} ₽</Text>
                        <Text className={styles.period}>&nbsp;{DISCOUNT_IN_OUTFLOW_PERIODS_TABLE[product.period]}</Text>
                    </Text>
                    <Text className={styles.oldPriceText}>вместо {discountInOutflowTextFormatter.format(product.price || 0)} ₽</Text>
                </div>
            </div>
        );
    }, []);

    if (isMobile) {
        return (
            <MobileDialog
                id="discount-in-outflow-modal-mobile"
                onClose={onCloseAction}
                closeOnDimmerClick
                scrollRef={scrollRef}
                mod="base"
                contextPadding="zero"
            >
                <div ref={scrollRef} className={styles.rootMobile}>
                    <div className={styles.imageContainer}>
                        <img className={styles.image} src={coverImageMobile} alt={title} />
                        {imgBanner}
                    </div>
                    <div className={styles.content}>
                        <Title>Закрепим за вами скидку&nbsp;{discountInOutflowValue}%</Title>
                        <Spacing size={8} />
                        <Text className={styles.text}>{text}</Text>
                    </div>

                    <div className={styles.buttonWrapper}>
                        <Button theme="vk" primary fluid onClick={onSuccessAction}>
                            Получить скидку
                        </Button>
                        <Spacing size={12} />
                        <Button theme="vk" fluid onClick={onCancelAction}>
                            Отключить
                        </Button>
                    </div>
                </div>
            </MobileDialog>
        );
    }

    return (
        <WhatsNewDialog
            imageUrl={coverImage}
            imageWrapperClassName={styles.imageWrapper}
            imageClassName={styles.image}
            title={title}
            content={content}
            imageAdditionalContent={imgBanner}
            primaryButtonText="Получить скидку"
            secondaryButtonText="Всё равно отключить"
            onSecondary={onCancelAction}
            onClick={onSuccessAction}
            onClose={onCloseAction}
            imageBgColor="var(--vkui--color_background_modal)"
            buttonTheme="vk"
            dialogSize="tiny"
            qaId="discount-in-outflow-modal"
            isRebranding
        />
    );
}
